import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Borrowed-chord lexicon entries — copy authored by Bowie in
 * `.claude/agent-memory/bowie/reference_theory_copy_register.md` under
 * "Borrowed-chord copy". Kept in a separate file from `lexicon-entries.ts`
 * so Bowie's polished Phase-1 starter set (cadences/modes/progressions) stays
 * untouched and these new entries are explicitly attributable to Bowie's
 * reference document.
 *
 * Category note: `'function'` is the closest existing fit — a borrowed chord
 * is a chord function in a non-diatonic key. A dedicated `'borrowed'`
 * category would require extending the lexicon schema; deferred to a later
 * milestone if Bowie wants finer-grained classification.
 *
 * Naming note: lexicon ids encode `borrowed-{degree}-{scale-quality}`. The
 * `flat-VII` form (rather than `b7` or `bVII`) keeps ids ASCII-safe for file
 * tools and grep without losing meaning — the displayed copy uses Unicode.
 *
 * Phase-1 coverage covers the five most-common major-key borrowings. Minor-
 * key borrowings (e.g., the Dorian-flavoured IV in natural minor) are
 * intentionally not shipped here; the analyzer's `getParallelModeLabel`
 * returns null for them, so surfaces will gracefully hide the row.
 */
export const BORROWED_LEXICON_ENTRIES: LexiconEntry[] = [
  {
    id: 'borrowed-flat-VII-major',
    category: 'function',
    displayName: '♭VII (borrowed from Mixolydian)',
    displayNameShort: '♭VII',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Borrowed sound — adds a rockier color than the key alone gives you.',
      intermediate: 'Borrowed from parallel Mixolydian — the ♭7 gives the rock/folk color.',
      advanced: 'Modal interchange — ♭VII borrowed from parallel Mixolydian.',
    },
    genreTags: ['rock', 'folk', 'pop'],
  },
  {
    id: 'borrowed-iv-major',
    category: 'function',
    displayName: 'iv (borrowed from parallel minor)',
    displayNameShort: 'iv',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Borrowed sound — adds a soft melancholy.',
      intermediate: 'Borrowed from parallel minor — the minor IV gives a tender pull.',
      advanced: 'Modal interchange — iv borrowed from parallel minor; plagal-minor.',
    },
    genreTags: ['pop', 'ballad'],
  },
  {
    id: 'borrowed-flat-III-major',
    category: 'function',
    displayName: '♭III (borrowed from parallel minor)',
    displayNameShort: '♭III',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Borrowed sound — drops in a warmer, solemn chord from the parallel minor.',
      intermediate: 'Borrowed from parallel minor — major chord built on the lowered third.',
      advanced: 'Modal interchange — ♭III borrowed from parallel minor; common in rock and cinematic harmony.',
    },
    genreTags: ['rock', 'pop', 'cinematic'],
  },
  {
    id: 'borrowed-flat-VI-major',
    category: 'function',
    displayName: '♭VI (borrowed from parallel minor)',
    displayNameShort: '♭VI',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Borrowed sound — a tender, often cinematic darker chord.',
      intermediate: 'Borrowed from parallel minor — major chord built on the lowered sixth.',
      advanced: 'Modal interchange — ♭VI borrowed from parallel minor; lowered-submediant color.',
    },
    genreTags: ['cinematic', 'pop'],
  },
  {
    id: 'borrowed-flat-II-major',
    category: 'function',
    displayName: '♭II (Neapolitan / Phrygian color)',
    displayNameShort: '♭II',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'Borrowed sound — adds a dramatic, Spanish-flavored color.',
      intermediate: 'Borrowed from parallel Phrygian — the ♭2 gives a dramatic half-step pull home.',
      advanced: 'Neapolitan — ♭II borrowed from parallel Phrygian; classical predominant.',
    },
    genreTags: ['flamenco', 'classical', 'film'],
  },
]
